import { getRegistryList } from '@/api/backend/registry';
import { RegistryType } from '@/api/backend/types';
import { ResponsiveDataView } from '@/components/responsive-data-view';
import { ZONE_CODES } from '@/lib/constants';
import { formatIsoDate, getLocaleObject } from '@/lib/utils';
import {
  Card,
  DataTable,
  DataTableColumnType,
  DataTableFilterConfig,
  DataTableSortConfig,
  Link,
} from '@opaz/ui-components';
import { useQuery } from '@tanstack/react-query';
import { useScrollToSection } from '@/hooks/use-scroll-to-section';
import { DASHBOARD_SECTIONS } from '@/lib/dashboard-sections';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const ServiceProvidersCard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  useScrollToSection(DASHBOARD_SECTIONS.serviceProviders);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortConfig, setSortConfig] = useState<DataTableSortConfig | null>(null);
  const [filterConfig, setFilterConfig] = useState<DataTableFilterConfig | null>(null);

  const { data: page, isLoading } = useQuery({
    queryKey: ['registries', 'RGT_SPR', currentPage, sortConfig, filterConfig],
    queryFn: () => getRegistryList('RGT_SPR', { page: currentPage, size: 5 }, sortConfig, filterConfig, {}),
  });

  const columns: DataTableColumnType<RegistryType>[] = [
    {
      title: t('common.id'),
      render: (record: RegistryType) => (
        <Link
          onClick={() => {
            navigate({
              to: '/registry/service-providers/$providerId',
              params: { providerId: record.registryNumber },
              search: { version: undefined },
            });
          }}
          className="w-[120px] text-start"
        >
          {record.registryNumber}
        </Link>
      ),
    },
    {
      title: t('dashboard.serviceProviders.headings.contractNumber'),
      sortKey: 'recordNumber',
      render: (record: RegistryType) => (
        <Link
          onClick={() => {
            navigate({
              to: '/registry/contracts/$contractId',
              params: { contractId: record.relatedRegistryNumber ?? '' },
              search: { version: undefined },
            });
          }}
          className="w-[120px] text-start"
        >
          {record.relatedRegistryNumber}
        </Link>
      ),
    },
    {
      title: t('dashboard.serviceProviders.headings.zone'),
      sortKey: 'zone',
      render: (record: RegistryType) =>
        record.zoneCode ? t(`common.zoneCode.${record.zoneCode}`) : t('common.unknown'),
      filter: {
        type: 'select',
        key: 'zone',
        options: ZONE_CODES.map((zoneCode) => ({
          label: t(`common.zoneCode.${zoneCode}`),
          value: zoneCode,
        })),
      },
    },
    {
      title: t('dashboard.serviceProviders.headings.party1'),
      sortKey: 'beneficiary',
      render: (record: RegistryType) => (i18n.language === 'ar' ? record.party1NameAr : record.party1NameEn),
    },
    {
      title: t('dashboard.serviceProviders.headings.party2'),
      sortKey: 'contractor',
      render: (record: RegistryType) => (i18n.language === 'ar' ? record.party2NameAr : record.party2NameEn),
    },
    {
      title: t('dashboard.serviceProviders.headings.providerCRNumber'),
      render: (record: RegistryType) => {
        return record.party1;
      },
    },
    {
      title: t('dashboard.serviceProviders.headings.startDate'),
      sortKey: 'entries.startDate',
      render: (record: RegistryType) => {
        return formatIsoDate(record.startDate, i18n.language);
      },
      filter: {
        type: 'date-range',
        key: 'startDateRange',
      },
    },
  ];

  return (
    <Card id={DASHBOARD_SECTIONS.serviceProviders} className="w-full scroll-mt-24 p-5 shadow-lg border-none">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold">{t('dashboard.tabs.providers')}</h2>
      </div>

      <ResponsiveDataView
        columns={columns}
        data={page?.content?.length ? [...page.content] : []}
        isLoading={isLoading}
        emptyMessage={t('common.table.emptyState')}
        currentPage={currentPage + 1}
        totalPages={page?.totalPages || 0}
        onPageChange={(page) => setCurrentPage(page - 1)}
        getRowKey={(record) => record.registryNumber}
        filterConfig={filterConfig}
        onFilterChange={(config) => {
          setCurrentPage(0);
          setFilterConfig(config);
        }}
      >
        <DataTable
          locale={getLocaleObject(i18n.language)}
          columns={columns}
          dataSource={page?.content?.length ? [...page.content] : []}
          isLoading={isLoading}
          currentPage={currentPage + 1}
          totalPages={page?.totalPages || 0}
          onPageChange={(page) => setCurrentPage(page - 1)}
          onSortChange={setSortConfig}
          onFilterChange={(config) => {
            setCurrentPage(0);
            setFilterConfig(config);
          }}
          dateFormatter={(value: Date) => formatIsoDate(value.toISOString(), i18n.language)}
          translations={{
            apply: t('common.table.filter.apply'),
            clear: t('common.table.filter.clear'),
            from: t('common.table.filter.from'),
            clearAllFilters: t('common.table.filter.clearAllFilters'),
            noData: t('common.table.emptyState'),
            nextPage: t('common.table.next'),
            previousPage: t('common.table.previous'),
          }}
        />
      </ResponsiveDataView>
    </Card>
  );
};
